import React, { useEffect, useState, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, listAll, getDownloadURL } from 'firebase/storage';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

const CamerasPage = ({ isMobile }) => {
  const auth = getAuth();
  const user = auth.currentUser;
  const [latestVideo, setLatestVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const playerRef = useRef(null); // Ref to store the video.js player instance
  const videoRef = useRef(null); // Ref to the video element

  // Fetch the latest video
  useEffect(() => {
    // Increment viewer count on mount
    fetch('https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/incrementViewerCount', {
      method: 'POST',
    });

    const fetchLatestVideo = async () => {
      setLoading(true);
      setError(null);

      const storage = getStorage();
      const recordingsRef = ref(storage, 'recordings');
      try {
        const fileList = await listAll(recordingsRef);
        if (fileList.items.length > 0) {
          const sortedFiles = fileList.items.sort((a, b) => b.name.localeCompare(a.name));
          const latestFile = sortedFiles[0];
          const url = await getDownloadURL(latestFile);
          setLatestVideo(url);
        } else {
          setError('No videos found in Firebase Storage.');
        }
      } catch (err) {
        console.error('Error fetching latest video:', err);
        setError('Failed to load video. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLatestVideo();

    const interval = setInterval(fetchLatestVideo, 5 * 60 * 1000);

    return () => {
      fetch('https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/decrementViewerCount', {
        method: 'POST',
      });
      clearInterval(interval);
    };
  }, []);

  // Initialize video.js player when latestVideo changes and the video element is rendered
  useEffect(() => {
    if (videoRef.current && latestVideo) {
      // Initialize the player if it doesn't exist
      if (!playerRef.current) {
        playerRef.current = videojs(videoRef.current, {
          controls: true,
          autoplay: true,
          sources: [{
            src: latestVideo,
            type: 'video/mp4',
          }],
        });
      } else {
        // Update the source if the player already exists
        playerRef.current.src({ src: latestVideo, type: 'video/mp4' });
        playerRef.current.play();
      }
    }

    // Cleanup on unmount or when latestVideo changes
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [latestVideo]);

  return (
    <div className="page-container">
      <style>
        {`
          .page-container {
            padding: 2rem;
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            position: relative;
            z-index: 1;
          }
          .page-title {
            font-size: 1.5rem;
            margin-bottom: 1rem;
            color: white;
          }
          .camera-section {
            background: rgba(255, 255, 255, 0.8);
            padding: 1rem;
            border-radius: 0.5rem;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            margin-bottom: 1rem;
            position: relative;
            z-index: 3;
          }
          .camera-title {
            font-size: 1.25rem;
            margin-bottom: 0.5rem;
          }
          .camera-feed {
            width: 100%;
            height: auto;
            border-radius: 0.25rem;
          }
          .instructions {
            margin-top: 1rem;
            color: #374151;
            font-size: 0.9rem;
          }
          .loading, .error {
            color: #374151;
            font-size: 1rem;
            text-align: center;
            margin-top: 1rem;
          }
          @media (max-width: 640px) {
            .page-container {
              padding: 1rem;
            }
            .page-title {
              font-size: 1.25rem;
            }
            .camera-section {
              padding: 0.75rem;
            }
            .camera-title {
              font-size: 1rem;
            }
            .instructions {
              font-size: 0.875rem;
            }
          }
        `}
      </style>
      <h1 className="page-title">Camera Recordings</h1>
      <div className="camera-section">
        <h2 className="camera-title">Argus 4 Pro Latest Recording</h2>
        {loading && <div className="loading">Loading video...</div>}
        {error && <div className="error">{error}</div>}
        {!loading && !error && latestVideo && (
          <video
            ref={videoRef}
            id="argus4-video"
            className="video-js camera-feed"
            width="100%"
            height="auto"
          />
        )}
        {!loading && !error && !latestVideo && (
          <div className="error">No video available to play.</div>
        )}
        <div className="instructions">
          <p><strong>Note:</strong> This displays the latest recorded video (updated on motion events every 5 minutes). If no video is available, ensure new videos are being generated.</p>
        </div>
      </div>
    </div>
  );
};

export default CamerasPage;